import React from 'react';
import { Helmet } from 'react-helmet-async';
import { PageVisibilityContent } from '../content/schema';
import { usePageVisibility } from '../hooks/usePageVisibility';
import { SiteShell } from '../shell/SiteShell';

interface PageVisibilityGateProps {
  pageKey: keyof PageVisibilityContent;
  children: React.ReactNode;
}

/**
 * Wraps a route with a CMS-driven visibility check.
 * If the keyed page is toggled off in /dashboard/content → Page visibility,
 * renders a minimal 404 page instead of children.
 * If visibility is still loading (Supabase not yet resolved) it renders
 * children immediately to avoid flash — the seed default is `true`.
 */
const PageVisibilityGate: React.FC<PageVisibilityGateProps> = ({ pageKey, children }) => {
  const { isVisible } = usePageVisibility();

  if (!isVisible(pageKey)) {
    return (
      <SiteShell>
        <Helmet>
          <title>Not found | subfocus.ai</title>
        </Helmet>
        <span className="sfp-eyebrow">404</span>
        <h1 className="sfp-title">This page isn't on the map.</h1>
        <p className="sfp-lede">It may have moved, or it isn't open right now.</p>
      </SiteShell>
    );
  }

  return <>{children}</>;
};

export default PageVisibilityGate;
